// pages/Cancelaciones.js
import React, { useState, useEffect, useRef } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Switch,
  InputNumber,
  Space,
  Typography,
  message,
  Tag,
  Tooltip,
  Row,
  Col,
  Popconfirm,
  Badge,
  Statistic,
  Divider,
  Card
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  BlockOutlined,
  UnlockOutlined,
  SearchOutlined,
  ReloadOutlined,
  EyeOutlined,
  ExportOutlined,
  CloseCircleOutlined,
  PercentageOutlined,
  ClockCircleOutlined
} from '@ant-design/icons';
import axios from 'axios';

import PageContainer from '../components/PageContainer';
import { BASE_URL } from '../utils/constants';

const { Title, Text } = Typography;

const Cancelaciones = () => {
  // Estados
  const [cancelaciones, setCancelaciones] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [globalSearchText, setGlobalSearchText] = useState('');
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalType, setModalType] = useState('create');
  const [currentCancelacion, setCurrentCancelacion] = useState(null);
  const [form] = Form.useForm();
  const [tableLoading, setTableLoading] = useState(false);
  
  // Estados para filtros y ordenación
  const [filteredInfo, setFilteredInfo] = useState({});
  const [sortedInfo, setSortedInfo] = useState({});

  // Cargar datos iniciales
  useEffect(() => {
    fetchCancelaciones();
  }, []);

  const fetchCancelaciones = async () => {
    setTableLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BASE_URL}/api/cancelaciones`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Transformar datos booleanos
      const transformedData = response.data.data.map(item => ({
        ...item,
        Activo: item.Activo === true || item.Activo === 1 || item.Activo === '1',
        Valor: parseFloat(item.Valor) || 0,
        Dias: parseFloat(item.Dias) || 0
      }));
      
      setCancelaciones(transformedData);
      setFilteredData(transformedData);
      
      console.log('✅ Políticas de cancelación cargadas:', transformedData);
    } catch (error) {
      message.error('Error al cargar las políticas de cancelación');
      console.error('Error fetchCancelaciones:', error);
    } finally {
      setTableLoading(false);
    }
  };

  // Función para búsqueda global
  const handleGlobalSearch = (searchText) => {
    setGlobalSearchText(searchText);
    
    if (!searchText.trim()) {
      setFilteredData(cancelaciones);
      return;
    }
    
    const lowerSearchText = searchText.toLowerCase().trim();
    
    const filtered = cancelaciones.filter(record => {
      return Object.keys(record).some(key => {
        const value = record[key];
        
        if (value === null || value === undefined) return false;
        
        const stringValue = String(value).toLowerCase();
        return stringValue.includes(lowerSearchText);
      });
    });
    
    setFilteredData(filtered);
  };

  // Configuración de columnas
  const columns = [
    {
      title: 'Código',
      dataIndex: 'AutoCodigo',
      key: 'AutoCodigo',
      width: 80,
      sorter: (a, b) => a.AutoCodigo - b.AutoCodigo,
      sortOrder: sortedInfo.columnKey === 'AutoCodigo' && sortedInfo.order,
      render: (codigo) => <Tag color="blue">#{codigo}</Tag>,
    },
    {
      title: 'Descripción',
      dataIndex: 'Descripcion',
      key: 'Descripcion',
      width: 200,
      sorter: (a, b) => a.Descripcion.localeCompare(b.Descripcion),
      sortOrder: sortedInfo.columnKey === 'Descripcion' && sortedInfo.order,
      ellipsis: true,
    },
    {
      title: 'Título',
      dataIndex: 'Titulo',
      key: 'Titulo',
      width: 150,
      sorter: (a, b) => (a.Titulo || '').localeCompare(b.Titulo || ''),
      sortOrder: sortedInfo.columnKey === 'Titulo' && sortedInfo.order,
    },
    {
      title: 'Valor (%)',
      dataIndex: 'Valor',
      key: 'Valor',
      width: 100,
      sorter: (a, b) => (a.Valor || 0) - (b.Valor || 0),
      sortOrder: sortedInfo.columnKey === 'Valor' && sortedInfo.order,
      render: (valor) => (
        <Tag color={valor > 0 ? 'red' : 'default'}>
          {valor > 0 ? `${valor}%` : '-'}
        </Tag>
      ),
    },
    {
      title: 'Días Antes',
      dataIndex: 'Dias',
      key: 'Dias',
      width: 120,
      sorter: (a, b) => (a.Dias || 0) - (b.Dias || 0),
      sortOrder: sortedInfo.columnKey === 'Dias' && sortedInfo.order,
      render: (dias) => (
        <Text type={dias > 0 ? 'warning' : 'secondary'}>
          {dias > 0 ? `${dias} días` : '-'}
        </Text>
      ),
    },
    {
      title: 'Estado',
      dataIndex: 'Activo',
      key: 'Activo',
      width: 100,
      render: (activo) => (
        <Badge
          status={activo ? 'success' : 'error'}
          text={activo ? 'Activo' : 'Inactivo'}
        />
      ),
      filters: [
        { text: 'Activas', value: true },
        { text: 'Inactivas', value: false },
      ],
      filteredValue: filteredInfo.Activo || null,
      onFilter: (value, record) => record.Activo === value,
    },
    {
      title: 'Acciones',
      key: 'acciones',
      width: 180,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Tooltip title="Editar">
            <Button
              type="primary"
              icon={<EditOutlined />}
              size="small"
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          
          <Tooltip title={record.Activo ? "Desactivar" : "Activar"}>
            <Button
              type={record.Activo ? "dashed" : "primary"}
              danger={record.Activo}
              icon={record.Activo ? <BlockOutlined /> : <UnlockOutlined />}
              size="small"
              onClick={() => handleToggleActive(record)}
            />
          </Tooltip>
          
          <Popconfirm
            title="¿Eliminar política de cancelación?"
            description="Esta acción no se puede deshacer"
            onConfirm={() => handleDelete(record.AutoCodigo)}
            okText="Sí"
            cancelText="No"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="Eliminar">
              <Button
                danger
                icon={<DeleteOutlined />}
                size="small"
              />
            </Tooltip>
          </Popconfirm>
          
          <Tooltip title="Ver detalle">
            <Button
              type="text"
              icon={<EyeOutlined />}
              size="small"
              onClick={() => handleView(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  // Handlers para las acciones
  const handleCreate = () => {
    setModalType('create');
    setCurrentCancelacion(null);
    form.resetFields();
    form.setFieldsValue({
      Activo: true,
      Valor: 0,
      Dias: 0
    });
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setModalType('edit');
    setCurrentCancelacion(record);
    
    form.setFieldsValue({
      ...record,
      Activo: record.Activo || false,
      Valor: record.Valor || 0,
      Dias: record.Dias || 0,
      Descripcion: record.Descripcion || '',
      Titulo: record.Titulo || ''
    });
    setModalVisible(true);
  };

  const handleView = (record) => {
    Modal.info({
      title: `Detalle de Política: ${record.Descripcion}`,
      width: 700,
      content: (
        <div style={{ marginTop: 20 }}>
          <Row gutter={[16, 16]}>
            <Col span={8}>
              <strong>Código Hotel:</strong> {record.CodigoHotel}
            </Col>
            <Col span={8}>
              <strong>Código:</strong> <Tag color="blue">#{record.AutoCodigo}</Tag>
            </Col>
            <Col span={8}>
              <strong>Estado:</strong> {record.Activo ? 'ACTIVA' : 'INACTIVA'}
            </Col>
            <Col span={24}>
              <strong>Descripción:</strong> {record.Descripcion}
            </Col>
            <Col span={24}>
              <strong>Título:</strong> {record.Titulo || '-'}
            </Col>
            <Col span={24}>
              <Divider orientation="left">Configuración de Cancelación</Divider>
            </Col>
            <Col span={12}>
              <Card size="small" title="Penalización">
                <Statistic
                  value={record.Valor || 0}
                  suffix="%"
                  valueStyle={{ color: record.Valor > 0 ? '#cf1322' : '#52c41a' }}
                />
              </Card>
            </Col>
            <Col span={12}>
              <Card size="small" title="Días Anticipación">
                <Statistic
                  value={record.Dias || 0}
                  suffix="días"
                  valueStyle={{ color: record.Dias > 0 ? '#fa8c16' : '#52c41a' }}
                />
              </Card>
            </Col>
            <Col span={24}>
              <Text type="secondary" style={{ fontSize: '12px', fontStyle: 'italic' }}>
                {record.Dias > 0 ? 
                  `Esta política aplica si se cancela con ${record.Dias} días o menos de anticipación` :
                  'Esta política aplica sin restricción de días'
                }
                {record.Valor > 0 && `, con una penalización del ${record.Valor}%`}
              </Text>
            </Col>
          </Row>
        </div>
      ),
      onOk() {},
    });
  };

  const handleToggleActive = async (record) => {
    try {
      setTableLoading(true);
      const token = localStorage.getItem('token');
      const nuevoEstado = !record.Activo;
      
      const response = await axios.patch(
        `${BASE_URL}/api/cancelaciones/${record.AutoCodigo}/estado`,
        { Activo: nuevoEstado },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const cancelacionActualizada = {
        ...response.data.data,
        Activo: response.data.data.Activo === true || response.data.data.Activo === 1 || response.data.data.Activo === '1',
        Valor: parseFloat(response.data.data.Valor) || 0,
        Dias: parseFloat(response.data.data.Dias) || 0
      };
      
      setCancelaciones(cancelaciones.map(c => 
        c.AutoCodigo === record.AutoCodigo ? cancelacionActualizada : c
      ));
      
      setFilteredData(prev => prev.map(c => 
        c.AutoCodigo === record.AutoCodigo ? cancelacionActualizada : c
      ));
      
      message.success(`Política ${nuevoEstado ? 'activada' : 'desactivada'} correctamente`);
      
    } catch (error) {
      message.error('Error al actualizar el estado');
      console.error('Error handleToggleActive:', error);
    } finally {
      setTableLoading(false);
    }
  };

  const handleDelete = async (autocodigo) => {
    try {
      setTableLoading(true);
      const token = localStorage.getItem('token');
      
      await axios.delete(
        `${BASE_URL}/api/cancelaciones/${autocodigo}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const nuevasCancelaciones = cancelaciones.filter(c => c.AutoCodigo !== autocodigo);
      setCancelaciones(nuevasCancelaciones);
      
      const nuevoFilteredData = filteredData.filter(c => c.AutoCodigo !== autocodigo);
      setFilteredData(nuevoFilteredData);
      
      message.success('Política de cancelación eliminada correctamente');
      
    } catch (error) {
      message.error(error.response?.data?.error || 'Error al eliminar la política de cancelación');
      console.error('Error handleDelete:', error);
    } finally {
      setTableLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();
      const token = localStorage.getItem('token');
      
      console.log('📝 Datos a guardar:', values);
      
      if (modalType === 'create') {
        const response = await axios.post(
          `${BASE_URL}/api/cancelaciones`,
          values,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        const nuevaCancelacion = {
          ...response.data.data,
          Activo: response.data.data.Activo === true || response.data.data.Activo === 1 || response.data.data.Activo === '1',
          Valor: parseFloat(response.data.data.Valor) || 0,
          Dias: parseFloat(response.data.data.Dias) || 0
        };
        
        setCancelaciones([...cancelaciones, nuevaCancelacion]);
        
        if (globalSearchText) {
          const lowerSearchText = globalSearchText.toLowerCase();
          const shouldShowInSearch = Object.keys(nuevaCancelacion).some(key => {
            const value = nuevaCancelacion[key];
            if (value === null || value === undefined) return false;
            return String(value).toLowerCase().includes(lowerSearchText);
          });
          
          if (shouldShowInSearch) {
            setFilteredData([...filteredData, nuevaCancelacion]);
          }
        } else {
          setFilteredData([...filteredData, nuevaCancelacion]);
        }
        
        message.success('Política de cancelación creada correctamente');
        
      } else {
        const response = await axios.put(
          `${BASE_URL}/api/cancelaciones/${currentCancelacion.AutoCodigo}`,
          values,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        const cancelacionActualizada = {
          ...response.data.data,
          Activo: response.data.data.Activo === true || response.data.data.Activo === 1 || response.data.data.Activo === '1',
          Valor: parseFloat(response.data.data.Valor) || 0,
          Dias: parseFloat(response.data.data.Dias) || 0
        };
        
        const nuevasCancelaciones = cancelaciones.map(c => 
          c.AutoCodigo === currentCancelacion.AutoCodigo ? cancelacionActualizada : c
        );
        
        setCancelaciones(nuevasCancelaciones);
        
        const nuevoFilteredData = filteredData.map(c => 
          c.AutoCodigo === currentCancelacion.AutoCodigo ? cancelacionActualizada : c
        );
        
        setFilteredData(nuevoFilteredData);
        
        message.success('Política de cancelación actualizada correctamente');
      }
      
      setModalVisible(false);
      form.resetFields();
      
    } catch (error) {
      console.error('❌ Error en handleSave:', error);
      
      if (error.response?.data?.error) {
        message.error(error.response.data.error);
      } else if (error.response?.data?.details) {
        message.error(error.response.data.details);
      } else {
        message.error('Error al guardar los datos');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (pagination, filters, sorter) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);
    
    let result = [...cancelaciones];
    
    Object.keys(filters).forEach(key => {
      const filterValues = filters[key];
      if (filterValues && filterValues.length > 0) {
        result = result.filter(item => {
          const itemValue = item[key];
          
          if (key === 'Activo') {
            return filterValues.includes(itemValue);
          }
          
          return true;
        });
      }
    });
    
    if (sorter.field && sorter.order) {
      result.sort((a, b) => {
        const aVal = a[sorter.field] || '';
        const bVal = b[sorter.field] || '';
        
        if (typeof aVal === 'string' && typeof bVal === 'string') {
          if (sorter.order === 'ascend') {
            return aVal.localeCompare(bVal);
          } else {
            return bVal.localeCompare(aVal);
          }
        } else {
          if (sorter.order === 'ascend') {
            return (aVal || 0) - (bVal || 0);
          } else {
            return (bVal || 0) - (aVal || 0);
          }
        }
      });
    }
    
    if (globalSearchText) {
      const lowerSearchText = globalSearchText.toLowerCase().trim();
      result = result.filter(record => {
        return Object.keys(record).some(key => {
          const value = record[key];
          if (value === null || value === undefined) return false;
          return String(value).toLowerCase().includes(lowerSearchText);
        });
      });
    }
    
    setFilteredData(result);
  };

  const clearFilters = () => {
    setFilteredInfo({});
    setSortedInfo({});
    setGlobalSearchText('');
    setFilteredData(cancelaciones);
    message.success('Filtros y búsqueda limpiados');
  };

  // Función para exportar
  const handleExport = () => {
    message.info('Función de exportación en desarrollo');
  };

  // Estadísticas
  const estadisticas = (
    <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
      <Col span={6}>
        <Statistic 
          title="Total Políticas" 
          value={cancelaciones.length}
          prefix={<CloseCircleOutlined />}
          valueStyle={{ color: '#1890ff' }}
        />
      </Col>
      <Col span={6}>
        <Statistic 
          title="Activas" 
          value={cancelaciones.filter(c => c.Activo).length}
          valueStyle={{ color: '#52c41a' }}
        />
      </Col>
      <Col span={6}>
        <Statistic 
          title="Con Penalización" 
          value={cancelaciones.filter(c => c.Valor > 0).length}
          prefix={<PercentageOutlined />}
          valueStyle={{ color: '#faad14' }}
        />
      </Col>
      <Col span={6}>
        <Statistic 
          title="Con Días Límite" 
          value={cancelaciones.filter(c => c.Dias > 0).length}
          prefix={<ClockCircleOutlined />}
          valueStyle={{ color: '#722ed1' }}
        />
      </Col>
    </Row>
  );

  const searchBar = (
    <div style={{ marginBottom: 16 }}>
      <Space>
        <Input.Search
          placeholder="Buscar por código, descripción, título, etc..."
          value={globalSearchText}
          style={{ width: 400 }}
          allowClear
          enterButton
          onChange={(e) => {
            setGlobalSearchText(e.target.value);
            if (e.target.value === '') {
              handleGlobalSearch('');
            }
          }}
          onSearch={handleGlobalSearch}
        />
        <Button
          icon={<ReloadOutlined />}
          onClick={clearFilters}
          size="small"
        >
          Limpiar
        </Button>
        <Tag color="blue" style={{ fontSize: 14, padding: '4px 12px', borderRadius: 20 }}>
          <strong>{filteredData.length}</strong> registros
        </Tag>
      </Space>
    </div>
  );

  return (
    <>
      <PageContainer
        title="Gestión de Políticas de Cancelación"
        subtitle={`${cancelaciones.length} políticas registradas`}
        icon={<CloseCircleOutlined />}
        loading={tableLoading}
        onRefresh={fetchCancelaciones}
        onCreate={handleCreate}
        onExport={handleExport}
        badgeCount={cancelaciones.length}
        badgeStatus="processing"
        extra={
          <Space>
            <Tag color="blue" style={{ fontSize: 14, padding: '4px 12px', borderRadius: 20 }}>
              <strong>{cancelaciones.length}</strong> políticas
            </Tag>
          </Space>
        }
        cardStyle={{
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)'
        }}
      >
        {/* Estadísticas */}
        {estadisticas}

        {searchBar}
        
        {/* Tabla */}
        <Table
          columns={columns}
          dataSource={filteredData}
          rowKey="AutoCodigo"
          loading={tableLoading}
          onChange={handleChange}
          scroll={{ x: 1200 }}
          style={{
            borderRadius: '8px',
            overflow: 'hidden'
          }}
        />
      </PageContainer>

      {/* Modal para crear/editar */}
      <Modal
        title={modalType === 'create' ? 'Nueva Política de Cancelación' : 'Editar Política de Cancelación'}
        open={modalVisible}
        onOk={handleSave}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        width={700}
        okText={modalType === 'create' ? 'Crear' : 'Guardar'}
        cancelText="Cancelar"
        confirmLoading={loading}
      >
        <Form
          form={form}
          layout="vertical"
        >
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="Descripcion"
                label="Descripción"
                rules={[{ required: true, message: 'Por favor ingrese la descripción' }]}
              >
                <Input placeholder="Descripción completa de la política" maxLength={200} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="Titulo"
                label="Título"
                rules={[{ required: true, message: 'Por favor ingrese el título' }]}
              >
                <Input placeholder="Título corto para mostrar" maxLength={100} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="Activo"
                label="Estado"
                valuePropName="checked"
              >
                <Switch checkedChildren="Activa" unCheckedChildren="Inactiva" />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">
            <PercentageOutlined /> Configuración de Cancelación
          </Divider>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="Valor"
                label="Penalización (%)"
                help="Porcentaje a cobrar por cancelación (0 para no penalización)"
              >
                <InputNumber 
                  placeholder="0" 
                  style={{ width: '100%' }}
                  min={0}
                  max={100}
                  formatter={value => `${value}%`}
                  parser={value => value.replace('%', '')}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="Dias"
                label="Días Anticipación"
                help="Días mínimos antes del check-in (0 para sin límite)"
              >
                <InputNumber 
                  placeholder="0" 
                  style={{ width: '100%' }}
                  min={0}
                  max={365}
                  addonAfter="días"
                />
              </Form.Item>
            </Col>
          </Row>

          <div style={{ marginTop: 16, padding: 12, backgroundColor: '#f6ffed', borderRadius: 6 }}>
            <Text type="secondary">
              <strong>💡 Ejemplo:</strong> Si configura "Penalización: 50%" y "Días Anticipación: 7", 
              significa que si se cancela con 7 días o menos de anticipación, se cobrará el 50% del total.
            </Text>
          </div>
        </Form>
      </Modal>
    </>
  );
};

export default Cancelaciones;